import { useEffect, useState } from "react";
import { api } from "./api";
import { Link } from "./Link";
import { Notice } from "./ui";

type LinkState = "valid" | "used" | "expired" | "closed";

/** The emailed upload link: names the account, signs in with one tap, or sends a fresh link. */
export function UploadLink({
  go,
  signedIn,
}: {
  go: (href: string) => void;
  signedIn: (next: string) => Promise<void>;
}) {
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get("token") ?? "");
  const [link, setLink] = useState<{ state: LinkState; email: string; next: string | null } | null>(null);
  const [problem, setProblem] = useState(token ? "" : "This upload link is incomplete. Open the whole link from your email.");
  const [busy, setBusy] = useState(false);
  const [renewed, setRenewed] = useState(false);
  useEffect(() => {
    // The token must not stay in the address bar or browser history.
    if (location.hash) history.replaceState({}, "", location.pathname);
    if (!token) return;
    let current = true;
    api<{ state: LinkState; email: string; next: string | null }>("/upload-links/peek", { token })
      .then((result) => current && setLink(result))
      .catch((e: Error) => current && setProblem(e.message));
    return () => {
      current = false;
    };
  }, [token]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setProblem("");
    try {
      await work();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const renew = () =>
    run(async () => {
      await api("/upload-links/renew", { token });
      setRenewed(true);
    });

  let body;
  if (problem && !link) body = <Notice error>{problem}</Notice>;
  else if (!link) body = <Notice>Checking your link…</Notice>;
  else if (link.next)
    body = (
      <>
        <p>You're already signed in as {link.email}.</p>
        <Link go={go} className="primary" href={link.next}>
          Open your review
        </Link>
      </>
    );
  else if (link.state === "closed")
    body = (
      <>
        <p>This review already has its footage, or it was cancelled. Sign in as {link.email} to see it.</p>
        <Link go={go} className="primary" href="/sign-in?next=%2Fme">
          Sign in
        </Link>
      </>
    );
  else if (renewed)
    body = <Notice>We sent a new upload link to {link.email}. It can take a minute to arrive.</Notice>;
  else if (link.state === "valid")
    body = (
      <>
        <p>
          This link signs you in as <strong>{link.email}</strong> on this device, so you can upload your footage. If that
          isn't your email address, close this page.
        </p>
        {problem && <Notice error>{problem}</Notice>}
        <button
          className="primary"
          disabled={busy}
          onClick={() =>
            run(async () => {
              const { next } = await api<{ next: string }>("/upload-links/redeem", { token });
              await signedIn(next);
            })
          }
        >
          {busy ? "Signing in…" : "Continue to upload"}
        </button>
      </>
    );
  else
    body = (
      <>
        <p>
          {link.state === "used" ? "This link was already used." : "This link has expired."} We can send a new one to{" "}
          {link.email}.
        </p>
        {problem && <Notice error>{problem}</Notice>}
        <button className="primary" disabled={busy} onClick={renew}>
          {busy ? "Sending…" : "Email me a new link"}
        </button>
      </>
    );

  return (
    <main id="main" className="app-main sign-in">
      <section className="stack">
        <h1>Upload your footage.</h1>
        {body}
      </section>
    </main>
  );
}
